import {cs} from "./chain-services";

export type Link<Expects, Provides = never> = (
    [Provides] extends [never]
        ? (unused?:any) => Expects
        : (next:Provides) => Expects
);

export type ComponentLink<
    Component extends ((...args:any) => any) | (new (...args:any) => any),
    LinkName extends string = "next",
> = (
    Component extends (...args:any) => any
        ? (
            [Parameters<Component>[0]] extends [{[key in LinkName]?: (v:any) => any}]
                ? Parameters<Component>[0][LinkName]
                : never
        )
        : (
            Component extends (new (...args:any) => any)
            ? (
                [ConstructorParameters<Component>[0]] extends [{[key in LinkName]?: (v:any) => any}]
                    ? ConstructorParameters<Component>[0][LinkName]
                    : never
            )
            : never
        )
);

export type ComponentNext<
    Component extends ((...args:any) => any) | (new (...args:any) => any),
    LinkName extends string = "next",
> = (
    ComponentLink<Component, LinkName> extends (...args:any) => any
        ? Parameters<ComponentLink<Component, LinkName>>[0]
        : never
);

export type Chain<Return, PendingReturn = never, VarStack extends {[k:string]: any} = {}> = {
    _nodes: any[],
    run(): Return;
    then<NextReturn>(node:(varStack:VarStack, next:Link<NextReturn>) => PendingReturn): Chain<Return, NextReturn, VarStack>;
    then(node:(varStack:VarStack) => PendingReturn): Chain<Return, never, VarStack>;
    then<
        NodeName extends string,
        NodeValue,
        NextReturn,
    >(
        nodeName : NodeName,
        node     : (varStack:VarStack, next:Link<NextReturn, NodeValue>) => PendingReturn,
    ): Chain<
        Return,
        NextReturn,
        (
            NodeName extends keyof VarStack
                ? Omit<VarStack, NodeName> & {[key in NodeName]: NodeValue}
                : {[key in keyof VarStack]: VarStack[key]} & {[key in NodeName]: NodeValue}
        )
    >;
};

export function makeChain<Return>(): Chain<Return, Return>;
export function makeChain(): Chain<any, any>
{
    return {
        _nodes : [],
        run    : () => null,
        then   : makeChainThen({nodes: []}),
    };
}

function extendChain(chain:{_nodes: any[]}, node:any): Chain<any, any, any> {
    const nodes = [...chain._nodes, node];
    return {
        _nodes : nodes,
        run    : () => cs(...nodes),
        then   : makeChainThen({nodes})
    };
}

function makeChainThen({nodes: _nodes})
{
    return (...args:[string, Function] | [Function]) =>
    {
        const nodeFn   = args.pop() as Function;
        const nodeName = args.pop() as string;
        return extendChain({_nodes}, nodeName ? [nodeName, nodeFn] as any : nodeFn);
    }
};

/**
 * Typescript Helpers
 */

/**
 * Helper for simple nodes that assign async values
 * @param valueFn Receives varStack, should return value to assign 
 */
export function asyncValueHelper<VarStack, PendingReturn, F extends (varStack:VarStack) => Promise<any>>(
    valueFn:F
): (varStack:VarStack, next:Link<PendingReturn, Awaited<ReturnType<F>>>) => Promise<PendingReturn>
{
    return async (varStack, next) => next(await valueFn(varStack));
}

/**
 * Helper for simple nodes that only assign simple values
 * @param valueFn Receives varStack, should return value to assign 
 */
export function valueHelper<VarStack, PendingReturn, F extends (varStack:VarStack) => any>(
    valueFn:F
): (varStack:VarStack, next:Link<PendingReturn, ReturnType<F>>) => PendingReturn
{
    return (varStack, next) => next(valueFn(varStack));
}
export const v = valueHelper;

/**
 * Create a helper for components that take `next`
 * @param linkName The name of `next` in the component
 */
export function makeComponentHelper<LinkName extends string>(linkName: LinkName)
{
    return <
        Props extends {[key in LinkName]: Link<any, any>},
        ComponentReturn,
    >(
        componentFn:(props:Props) => ComponentReturn,
    ): (
        <VarStack, Return>(
            s:(varStack:VarStack, component:(props:Omit<Props, LinkName>) => ComponentReturn) => Return
        ) => (
            (varStack:VarStack, next:Props[LinkName]) => Return
        )
    ) => {
        return (s) => (varStack, next) => s(varStack, (props) => componentFn({...props, [linkName]: next} as any));
    }
}

export const mch                     = makeComponentHelper;
export const componentHelper         = makeComponentHelper("next");
export const ch                      = componentHelper;
export const rootNextComponentHelper = makeComponentHelper("rootNext");
export const rnch                    = rootNextComponentHelper;

